import { Switch } from "@reactive-resume/ui";
import type { NotificationCategory } from "@reactive-resume/dto";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import {
  useNotificationPreferences,
  usePushNotifications,
  useUpdateNotificationPreference,
} from "@/client/services/notification";

const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  transactional: "Transactions & retraits",
  finance: "Paiements, packs & crédits",
  referral: "Parrainage & commissions",
  ai: "IA, traduction & exports",
  account: "Sécurité du compte",
  social: "Activité sur mes CV",
  system: "Annonces système",
  campaign: "Communications marketing",
  lifecycle: "Rappels d'activité et conseils",
};

/**
 * PARAMÈTRES NOTIFICATIONS — abonnement push (VAPID) + matrice catégorie × canal.
 * Monté dans Paramètres → Notifications. UX progressive : l'activation du push
 * se fait explicitement (jamais au premier chargement).
 */
export const NotificationSettings = () => {
  const { toast } = useToast();
  const [busyLabel, setBusyLabel] = useState<string | null>(null);

  const push = usePushNotifications();
  const { data: preferenceData } = useNotificationPreferences();
  const updatePreference = useUpdateNotificationPreference();

  const preferences = preferenceData ?? [];

  // ── Abonnement push ──────────────────────────────────────────────────────
  const handlePushToggle = async () => {
    setBusyLabel("push");
    const result = push.subscribed ? await push.unsubscribe() : await push.subscribe();
    setBusyLabel(null);

    if (result.ok) {
      toast({
        title: push.subscribed ? "Notifications push désactivées" : "Notifications push activées",
        description: push.subscribed
          ? "Vos appareils ne recevront plus de notifications hors-app."
          : "Vous recevrez désormais les alertes importantes même app fermée.",
      });
    } else if (result.reason === "denied") {
      toast({
        title: "Notifications bloquées par le navigateur",
        description:
          "Cliquez sur le cadenas 🔒 dans la barre d'adresse, autorisez les notifications pour ce site, puis réessayez.",
        variant: "error",
        duration: 6000,
      });
    } else if (result.reason) {
      toast({
        title: "Push non activé",
        description: "Vérifiez que le navigateur autorise les notifications, puis réessayez.",
        variant: "error",
      });
    }
  };

  // ── Préférence catégorie × canal ─────────────────────────────────────────
  const handlePreferenceChange = (
    category: NotificationCategory,
    channel: "inApp" | "push" | "email",
    value: boolean,
  ) => {
    updatePreference.mutate({ category, data: { [channel]: value } });
  };

  return (
    <div className="flex flex-col gap-8">
      {/* ── Notifications push ──────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-border-subtle bg-surface p-5 shadow-[0_1px_3px_rgba(20,14,44,0.04)]">
        <div className="flex items-start justify-between gap-6">
          <div className="max-w-md">
            <p className="text-sm font-semibold text-text">Notifications push</p>
            <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
              {!push.browserSupported
                ? "Votre navigateur ne prend pas en charge les notifications push."
                : !push.serverEnabled
                  ? "Le service de notifications push n'est pas encore configuré par l'administrateur du serveur."
                  : "Recevez les alertes importantes (paiement, retrait, récompenses) même lorsque l'application est fermée."}
            </p>
            {push.permission === "denied" && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50/60 p-3.5">
                <p className="text-xs font-bold text-red-600">
                  Les notifications sont bloquées par votre navigateur.
                </p>
                <ol className="mt-1.5 list-decimal space-y-1 pl-4 text-xs leading-relaxed text-text-muted">
                  <li>
                    Cliquez sur l'icône cadenas <span className="font-semibold">🔒</span> dans la
                    barre d'adresse.
                  </li>
                  <li>
                    Dans le menu « Notifications », choisissez{" "}
                    <span className="font-semibold">Autoriser</span>.
                  </li>
                  <li>Revenez ici et cliquez sur « Vérifier de nouveau ».</li>
                </ol>
                <button
                  type="button"
                  onClick={() => void push.retry()}
                  className="mt-2.5 rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-xs font-semibold text-text transition-colors hover:bg-canvas-subtle"
                >
                  J'ai autorisé — Vérifier de nouveau
                </button>
              </div>
            )}
            {push.subscribed && (
              <p className="mt-2 text-xs font-medium text-emerald-600">
                Ce navigateur est abonné aux notifications push.
              </p>
            )}
          </div>

          {push.supported && (
            <Switch
              checked={push.subscribed}
              disabled={push.busy || busyLabel === "push" || push.permission === "denied"}
              onCheckedChange={() => void handlePushToggle()}
              aria-label="Activer les notifications push"
            />
          )}
        </div>
      </section>

      {/* ── Préférences par catégorie ───────────────────────────────────────── */}
      <section>
        <p className="text-sm font-semibold text-text">Recevoir par catégorie</p>
        <p className="mt-1 text-[13px] text-text-muted">
          Choisissez ce que vous recevez, et par quel canal.
        </p>

        <div className="mt-4 overflow-hidden rounded-2xl border border-border-subtle bg-surface shadow-[0_1px_3px_rgba(20,14,44,0.04)]">
          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-5 border-b border-border-subtle bg-canvas-subtle/40 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] text-text-muted">
            <span>Catégorie</span>
            <span className="w-16 text-center">In-app</span>
            <span className="w-16 text-center">Push</span>
            <span className="w-16 text-center">E-mail</span>
          </div>

          {preferences.map((preference) => (
            <div
              key={preference.category}
              className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-5 border-b border-border-subtle/60 px-5 py-3.5 transition-colors last:border-b-0 hover:bg-canvas-subtle/40"
            >
              <span className="text-sm font-medium text-text">
                {CATEGORY_LABELS[preference.category as NotificationCategory]}
              </span>

              {(["inApp", "push", "email"] as const).map((channel) => {
                const isSecurityScope = preference.category === "account";
                const disabled = isSecurityScope && (channel === "push" || channel === "email");

                return (
                  <div key={channel} className="flex w-16 justify-center">
                    {channel === "inApp" ? (
                      <span className="text-[11px] font-semibold text-text-muted">Toujours</span>
                    ) : (
                      <Switch
                        checked={preference[channel]}
                        disabled={disabled || updatePreference.isPending}
                        onCheckedChange={(value) =>
                          handlePreferenceChange(
                            preference.category as NotificationCategory,
                            channel,
                            value,
                          )
                        }
                        aria-label={`${channel === "push" ? "Push" : "E-mail"} — ${CATEGORY_LABELS[preference.category as NotificationCategory]}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <p className="mt-3 text-xs text-text-muted">
          Les e-mails liés à la sécurité (mot de passe, double authentification) sont toujours
          envoyés.
        </p>
      </section>
    </div>
  );
};
